import { signal } from "@preact/signals";
import { api } from "../net/api.js";
import type { AdNetwork } from "./network.js";

/**
 * Light ads for people without the Obean Pass (ADR 0026): a banner at the top of solo and room
 * pages, and in solo a full-screen ad after a mission is lost three times in a row. House ads only
 * for now: Obean's own, no third party, nothing to consent to. An ad network comes later
 * (P4-AD-02).
 */

export interface HouseAd {
  id: string;
  text: string;
  cta: string;
  href: string;
}

/** Obean's own ads: the Pass, a game to try, the Learn pillar. */
export const HOUSE_ADS: readonly HouseAd[] = [
  {
    id: "pass",
    text: "Obean Pass: no ads, for you and every room you host.",
    cta: "See the Pass",
    href: "/pass",
  },
  {
    id: "game-beantumble",
    text: "Beantumble: drop the pods, fill a row, together or head to head.",
    cta: "Play it solo",
    href: "/solo?game=beantumble",
  },
  {
    id: "game-beanguard",
    text: "Beanguard: two to four tanks, one base. Hold the line together.",
    cta: "Play it solo",
    href: "/solo?game=beanguard",
  },
  {
    id: "learn",
    text: "Beanwords: guess what a word means, and learn a few new ones.",
    cta: "Try it",
    href: "/solo?game=beanwords",
  },
];

/** A house ad, not the one for the game being played: `pick` is a number in [0, 1). */
export function houseAd(pick: number, playing?: string): HouseAd {
  const choices = HOUSE_ADS.filter((a) => a.id !== `game-${playing ?? ""}`);
  return choices[Math.floor(pick * choices.length)] ?? (HOUSE_ADS[0] as HouseAd);
}

/** The full-screen ad is always the Pass: it's the way out of ads. */
export const FULL_AD = HOUSE_ADS[0] as HouseAd;

/** Whether this person sees ads: `/api/me` says (null until it answers, so a Pass member never sees a flash). */
export const adsForMe = signal<boolean | null>(null);
/** The network serving them, if any (ADR 0026); null: Obean's own house ads. */
export const adNetworkForMe = signal<AdNetwork | null>(null);
let asked = false;

/** Asks `/api/me` once per page. Failing, no ads: we can't tell, so we don't. */
export function loadAds(): void {
  if (asked) return;
  asked = true;
  api.me().then(
    (me) => {
      adNetworkForMe.value = me.ads === true ? (me.adNetwork ?? null) : null;
      adsForMe.value = me.ads === true;
    },
    () => {
      adsForMe.value = false;
    },
  );
}

// ───────────────────────── solo: three losses in a row bring a full-screen ad

/** Losses in a row on one mission before the full-screen ad (ADR 0026; tuned from data). */
export const LOSSES_BEFORE_AD = 3;
const KEY = "obean.solo.losses";
/** Seconds before the full-screen ad can be skipped. */
export const SKIP_AFTER_S = 5;

type Losses = Record<string, number>;
const keyOf = (gameId: string, missionId: string) => `${gameId}:${missionId}`;
/** The counts without one mission's. */
const without = (losses: Losses, k: string): Losses =>
  Object.fromEntries(Object.entries(losses).filter(([key]) => key !== k));

function read(): Losses {
  try {
    const raw = JSON.parse(localStorage.getItem(KEY) ?? "{}") as unknown;
    return raw && typeof raw === "object" ? (raw as Losses) : {};
  } catch {
    return {};
  }
}

function write(losses: Losses): void {
  try {
    localStorage.setItem(KEY, JSON.stringify(losses));
  } catch {
    // Not stored: the count starts over.
  }
}

/** A solo result: a loss adds one, a win clears the mission's count. */
export function countSoloResult(gameId: string, missionId: string, result: "win" | "loss"): void {
  const losses = read();
  const k = keyOf(gameId, missionId);
  write(result === "win" ? without(losses, k) : { ...losses, [k]: (losses[k] ?? 0) + 1 });
}

/** Whether the next try at this mission comes after a full-screen ad. */
export const fullAdDue = (gameId: string, missionId: string): boolean =>
  (read()[keyOf(gameId, missionId)] ?? 0) >= LOSSES_BEFORE_AD;

/** The ad was shown: the count starts over. */
export function fullAdShown(gameId: string, missionId: string): void {
  write(without(read(), keyOf(gameId, missionId)));
}
